import * as React from 'react';
import { X } from 'lucide-react';
import {
  AutosaveField,
  PanelSection,
  SidePanel,
  WorkspaceButton,
  WorkspaceNotice,
  useToast,
  workspaceFieldClass,
} from '@/components/ui/workspace';
import { AreaChip } from '@/components/ui/area-chip';
import {
  draftApi,
  type FrameworkInterviewType,
  type InterviewTypePatch,
  type PlanRole,
} from '@/lib/api/discovery-frameworks';
import { cn } from '@/lib/utils/cn';
import { tryAdd, type EditorCtx } from './editor';
import {
  DURATIONS,
  choicesWith,
  isActive,
  messageOf,
  parseKeywords,
  rolesText,
  shown,
} from './framework-logic';
import { FieldHelp, FlagToggle, InlineAdd, QuestionList, SaveStatus, ToggleChip } from './parts';
import { P_TEXT } from './area-panel';

/**
 * Side panel for one interview type (prototype `fwTypePanel()`): name,
 * job-title keywords, areas, its own questions and on-screen asks,
 * questionnaire, length, and the roles it puts in a new engagement's
 * starting plan (Decision 53.9), which the prototype shows read-only and
 * the build makes an editable list (E12-F01 scenario 8).
 */

const SELECT = cn(workspaceFieldClass, 'h-9 pr-8');

export function TypePanel({
  ctx,
  type,
  onClose,
}: {
  ctx: EditorCtx;
  type: FrameworkInterviewType;
  onClose: () => void;
}) {
  const { content, editing, busy, write } = ctx;
  const { toast } = useToast();
  const retired = type.status === 'retired';
  const canEdit = editing && !retired;
  const patch = (p: InterviewTypePatch) =>
    write((id, base) => draftApi.patchInterviewType(id, base, type.id, p), { what: type.code });

  const all = content.type_questions[type.id] ?? [];
  const questions = shown(
    all.filter((q) => q.kind !== 'on_screen'),
    editing,
  );
  const asks = shown(
    all.filter((q) => q.kind === 'on_screen'),
    editing,
  );
  const roles = content.plan_roles[type.id] ?? [];
  const areaById = new Map(content.areas.map((a) => [a.id, a]));
  const pickable = content.areas.filter((a) => isActive(a) || type.area_ids.includes(a.id));
  const questionnaires = content.questionnaires.filter(
    (q) => isActive(q) || q.id === type.questionnaire_id,
  );
  const questionnaire = content.questionnaires.find((q) => q.id === type.questionnaire_id);

  const addQuestion = (kind: 'question' | 'on_screen') => async (text: string) => {
    const { result, refusal } = await tryAdd(
      write,
      (id, base) => draftApi.addTypeQuestion(id, base, type.id, kind, text),
      type.code,
      (err) => messageOf(err, 'It could not be added.'),
    );
    if (result?.code) toast({ message: `${result.code} added to the draft.` });
    return refusal;
  };
  const setQuestion =
    (field: 'text' | 'status') => (q: { id: string; code: string }, value: string) =>
      void write(
        (id, base) =>
          draftApi.patchQuestion(
            id,
            base,
            q.id,
            field === 'text' ? { text: value } : { status: value as 'active' | 'retired' },
          ),
        { what: q.code },
      );

  return (
    <SidePanel
      aria-label={`Interview type ${type.code}`}
      identifier={type.code}
      path={<span className="text-body-sm font-semibold text-ink-900">{type.name}</span>}
      onClose={onClose}
      footer={
        editing ? (
          <>
            <SaveStatus busy={busy} />
            {retired ? (
              <WorkspaceButton disabled={busy} onClick={() => void patch({ status: 'active' })}>
                Restore interview type
              </WorkspaceButton>
            ) : (
              <WorkspaceButton
                variant="destructive"
                disabled={busy}
                onClick={() => void patch({ status: 'retired' })}
              >
                Retire interview type
              </WorkspaceButton>
            )}
          </>
        ) : undefined
      }
    >
      {retired ? (
        <WorkspaceNotice>
          Retired. Once published it is no longer offered for interviews added from then on;
          interviews already planned keep it.
        </WorkspaceNotice>
      ) : null}
      <PanelSection label="Name">
        {canEdit ? (
          <AutosaveField
            required
            aria-label="Name"
            value={type.name}
            maxLength={120}
            onSave={(name) => void patch({ name })}
          />
        ) : (
          <p className={P_TEXT}>{type.name}</p>
        )}
      </PanelSection>
      <PanelSection label="Suggested for job titles containing">
        {canEdit ? (
          <>
            <AutosaveField
              aria-label="Suggested for job titles containing"
              value={type.title_keywords.join(', ')}
              placeholder="e.g. grants, grant manager"
              onSave={(text) => void patch({ title_keywords: parseKeywords(text) })}
            />
            <FieldHelp>
              Comma-separated. Matched against the interviewee&rsquo;s title and department.
            </FieldHelp>
          </>
        ) : (
          <p className="text-body-sm text-ink-900">{type.title_keywords.join(', ') || '—'}</p>
        )}
      </PanelSection>
      <PanelSection label="Areas it covers">
        <div className="flex flex-wrap gap-1.5">
          {canEdit
            ? pickable.map((a) => {
                const on = type.area_ids.includes(a.id);
                return (
                  <ToggleChip
                    key={a.id}
                    on={on}
                    disabled={busy}
                    title={a.name}
                    onClick={() =>
                      void patch({
                        area_ids: on
                          ? type.area_ids.filter((x) => x !== a.id)
                          : [...type.area_ids, a.id],
                      })
                    }
                  >
                    {a.label}
                  </ToggleChip>
                );
              })
            : type.area_ids.map((id) => (
                <AreaChip key={id} title={areaById.get(id)?.name}>
                  {areaById.get(id)?.label ?? 'Unknown area'}
                </AreaChip>
              ))}
          {!canEdit && type.area_ids.length === 0 ? (
            <span className="text-meta text-ink-500">None</span>
          ) : null}
        </div>
      </PanelSection>
      <PanelSection label="Its own questions">
        <QuestionList
          items={questions}
          editing={canEdit}
          busy={busy}
          kind="question"
          addLabel="Question"
          onSave={setQuestion('text')}
          onStatus={setQuestion('status')}
          onAdd={addQuestion('question')}
        />
        <FieldHelp>Each area adds its own questions as well (Areas tab).</FieldHelp>
      </PanelSection>
      <PanelSection label="Ask to see on screen">
        <QuestionList
          items={asks}
          editing={canEdit}
          busy={busy}
          kind="on_screen"
          addLabel="On-screen ask"
          onSave={setQuestion('text')}
          onStatus={setQuestion('status')}
          onAdd={addQuestion('on_screen')}
        />
      </PanelSection>
      <div className="flex items-start gap-3">
        <PanelSection label="Questionnaire" className="min-w-0 flex-1">
          {canEdit ? (
            <select
              aria-label="Questionnaire"
              value={type.questionnaire_id ?? ''}
              disabled={busy}
              onChange={(e) => void patch({ questionnaire_id: e.target.value || null })}
              className={SELECT}
            >
              <option value="">None</option>
              {questionnaires.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.code} · {q.name}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-body-sm text-ink-900">
              {questionnaire ? `${questionnaire.code} · ${questionnaire.name}` : 'None'}
            </p>
          )}
        </PanelSection>
        <PanelSection label="Length" className="w-[110px] flex-none">
          {canEdit ? (
            <select
              aria-label="Length"
              value={type.duration_minutes}
              disabled={busy}
              onChange={(e) => void patch({ duration_minutes: Number(e.target.value) })}
              className={SELECT}
            >
              {choicesWith(DURATIONS, type.duration_minutes).map((m) => (
                <option key={m} value={m}>
                  {m} min
                </option>
              ))}
            </select>
          ) : (
            <p className="text-body-sm text-ink-900">{type.duration_minutes} min</p>
          )}
        </PanelSection>
      </div>
      <PanelSection label="In a new engagement's starting plan">
        {canEdit ? (
          <PlanRolesEditor
            roles={roles}
            busy={busy}
            onChange={(next) =>
              write((id, base) => draftApi.putPlanRoles(id, base, type.id, next), {
                what: `The starting plan of ${type.code}`,
              })
            }
          />
        ) : (
          <p className="text-body-sm text-ink-900">
            {roles.length > 0 ? rolesText(roles) : 'No. Consultants add it when they need it.'}
          </p>
        )}
      </PanelSection>
    </SidePanel>
  );
}

/** The starting plan as an editable list of roles; every change saves the whole list. */
function PlanRolesEditor({
  roles,
  busy,
  onChange,
}: {
  roles: readonly PlanRole[];
  busy: boolean;
  onChange: (next: Array<{ role: string; optional: boolean }>) => Promise<unknown>;
}) {
  const list = roles.map((r) => ({ role: r.role, optional: r.optional }));
  const replace = (i: number, next: { role: string; optional: boolean } | null) =>
    void onChange(next ? list.map((r, j) => (j === i ? next : r)) : list.filter((_, j) => j !== i));
  return (
    <div className="flex flex-col gap-1.5">
      {list.map((r, i) => (
        <div key={`${i}:${r.role}`} className="flex items-center gap-1.5">
          <AutosaveField
            required
            aria-label={`Role ${i + 1}`}
            value={r.role}
            maxLength={120}
            onSave={(role) => replace(i, { ...r, role })}
          />
          <FlagToggle
            on={r.optional}
            readOnly={busy}
            onToggle={() => replace(i, { ...r, optional: !r.optional })}
          >
            Optional
          </FlagToggle>
          <button
            type="button"
            aria-label={`Remove ${r.role}`}
            disabled={busy}
            onClick={() => replace(i, null)}
            className="inline-flex h-[26px] w-[26px] flex-none items-center justify-center rounded-chip text-ink-500 hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      ))}
      <div>
        <InlineAdd
          trigger="chip"
          label="Role"
          inputLabel="New role"
          placeholder="Role, e.g. Revenue Manager"
          busy={busy}
          onAdd={async (role) =>
            (await onChange([...list, { role, optional: false }]))
              ? null
              : 'The role was not added.'
          }
        />
      </div>
      <FieldHelp>
        Each role becomes a planned interview when an engagement starts discovery. Leave it empty
        and consultants add this type when they need it.
      </FieldHelp>
    </div>
  );
}
